/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQuery } from '@tanstack/react-query'
import { ErrorModel } from '@/shared/api/BaseAPI.model'
import { useSnackbar } from '@/shared/components/Snackbar/SnackbarProvider'
import { queryClient } from 'src/main'
import { useTranslation } from 'react-i18next'
import MobileAppManagementAPI from '../api/MobileAppManagementAPI'
import { MobileAppDataModel, QUERY } from '../api/MobileAppManagementAPI.model'

function useMobileAppManagementQuery() {
  const { showSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const logoImage = useQuery<any, ErrorModel>({
    queryKey: ['logo'],
    queryFn: MobileAppManagementAPI.getLogoImage
  })

  const backgroundImage = useQuery<any, ErrorModel>({
    queryKey: ['background'],
    queryFn: MobileAppManagementAPI.getBackgroundImage
  })

  const mobileAppData = useQuery<MobileAppDataModel, ErrorModel>({
    queryKey: [QUERY.MOBILE_APP_DATA],
    queryFn: MobileAppManagementAPI.getMobileAppData
  })

  const { mutate: postMobileAppData } = useMutation<
    MobileAppDataModel,
    ErrorModel,
    any
  >({
    mutationFn: (data: any) => MobileAppManagementAPI.postMobileAppData(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY.MOBILE_APP_DATA] })
      showSnackbar(
        t('ALERTS.POST_MOBILE_APP_SETTINGS_SUCCESS.TITLE'),
        t('ALERTS.POST_MOBILE_APP_SETTINGS_SUCCESS.DESCRIPTION'),
        'success'
      )
    },
    onError: () => {
      showSnackbar(
        t('ALERTS.POST_MOBILE_APP_SETTINGS_ERROR.TITLE'),
        t('ALERTS.POST_MOBILE_APP_SETTINGS_ERROR.DESCRIPTION'),
        'error'
      )
    }
  })

  const { mutate: postLogoImage } = useMutation<any, ErrorModel, any>({
    mutationFn: (data: any) => MobileAppManagementAPI.setLogoImage(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['logo'] })
      showSnackbar(
        t('ALERTS.POST_LOGO_IMAGE_UPLOAD_SUCCESS.TITLE'),
        t('ALERTS.POST_LOGO_IMAGE_UPLOAD_SUCCESS.DESCRIPTION'),
        'success'
      )
    },
    onError: () => {
      showSnackbar(
        t('ALERTS.POST_LOGO_IMAGE_UPLOAD_ERROR.TITLE'),
        t('ALERTS.POST_LOGO_IMAGE_UPLOAD_ERROR.DESCRIPTION'),
        'error'
      )
    }
  })

  const { mutate: postBackgroundImage } = useMutation<any, ErrorModel, any>({
    mutationFn: (data: any) => MobileAppManagementAPI.setBackgroundImage(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['background'] })
      showSnackbar(
        t('ALERTS.POST_BACKGROUND_IMAGE_UPLOAD_SUCCESS.TITLE'),
        t('ALERTS.POST_BACKGROUND_IMAGE_UPLOAD_SUCCESS.DESCRIPTION'),
        'success'
      )
    },
    onError: () => {
      showSnackbar(
        t('ALERTS.POST_BACKGROUND_IMAGE_UPLOAD_ERROR.TITLE'),
        t('ALERTS.POST_BACKGROUND_IMAGE_UPLOAD_ERROR.DESCRIPTION'),
        'error'
      )
    }
  })

  /*
    The whole mutation rather than just its trigger: extracting a palette is a
    round trip to another service and the form has nothing to show while it
    runs, so the caller needs the pending, done and failed states to say so.
  */
  const {
    mutateAsync: extractColorsSchemeFromImage,
    isPending: isExtractingColors,
    isSuccess: hasExtractedColors,
    isError: hasExtractColorsFailed,
    reset: resetExtractColorsState
  } = useMutation<any, ErrorModel, File>({
    mutationFn: (file: File) =>
      MobileAppManagementAPI.extractColorsSchemeFromImage(file),
    onError: () => {
      showSnackbar(
        t('ALERTS.EXTRACT_COLORS_ERROR.TITLE'),
        t('ALERTS.EXTRACT_COLORS_ERROR.DESCRIPTION'),
        'error'
      )
    }
  })

  return {
    logoImage,
    backgroundImage,
    mobileAppData,
    postMobileAppData,
    postLogoImage,
    postBackgroundImage,
    extractColorsSchemeFromImage,
    isExtractingColors,
    hasExtractedColors,
    hasExtractColorsFailed,
    resetExtractColorsState
  }
}
export default useMobileAppManagementQuery
